import { AttachmentApi } from '@api/twin/AttachmentApi';
import { useTeiaViewerContext } from '@stereograph/teiaviewer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePluginTranslation } from '@translation';
import DOMPurify from 'dompurify';
import { AnomalyDocument } from '../components/document/AnomalyDocumentCard';
import { useAnomalySectionContext } from '../context/AnomalySectionContext';
import { anomalyQueryKeys } from '../query-keys';

export const useDeleteAnomalyDocument = () => {
  const queryClient = useQueryClient();
  const { viewer } = useTeiaViewerContext();
  const { twinApiClient } = viewer;

  const { anomaly } = useAnomalySectionContext();
  const { t } = usePluginTranslation();

  const { mutate: deleteAnomalyDocument } = useMutation({
    mutationFn: async (params: {
      projectId: number;
      document: AnomalyDocument;
      commentId?: number;
    }) => await AttachmentApi(twinApiClient).delete(params.projectId, params.document.id),
    onMutate: () => {
      return viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'pending',
        message: t('anomaly_plugin.document.delete.modal.notification.success_text'),
        title: t('anomaly_plugin.document.delete.modal.notification.notification_title'),
        displayed: true,
      });
    },
    onSuccess: async (_data, variables, notificationId) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.documents(anomaly.id),
        }),
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.history_default(anomaly.id),
        }),
      ]);

      if (variables.commentId) {
        await queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.commentDocuments(variables.commentId),
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.commentDocuments_default(),
        });
      }

      viewer.notificationCenter.pushNotification(
        {
          type: t('anomaly_plugin.notification_type_text'),
          status: 'success',
          message: t('anomaly_plugin.document.delete.modal.notification.success_text', {
            file: DOMPurify.sanitize(variables.document.title),
          }),
          title: t('anomaly_plugin.document.delete.modal.notification.notification_title'),
          displayed: true,
        },
        notificationId,
      );
    },
    onError: (_data, variables, notificationId) => {
      viewer.notificationCenter.pushNotification(
        {
          type: t('anomaly_plugin.notification_type_text'),
          status: 'error',
          message: t('anomaly_plugin.document.delete.modal.notification.error_text', {
            file: DOMPurify.sanitize(variables.document.title),
          }),
          title: t('anomaly_plugin.document.delete.modal.notification.notification_title'),
          displayed: true,
        },
        notificationId,
      );
    },
  });

  return {
    deleteAnomalyDocument,
  };
};
